import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { Modal } from 'react-bootstrap';
import Draggable, { ControlPosition, DraggableData, DraggableEvent } from 'react-draggable';
import { Resizable, ResizeCallbackData } from 'react-resizable';
import { createPortal } from 'react-dom';

import { CollapseIcon, ExitPopupIcon, ExpandIcon, MinimizeIcon } from 'assets/icons';

import 'react-resizable/css/styles.css';

type TToggleableModalState = 'open' | 'expanded' | 'minimized';

interface IToggleableModalProps {
  title: string;
  onClose: () => void;
  autoWidth?: boolean;
  containerId?: string;
  modalClass?: string;
  children?: React.ReactNode;
}

const MIN_HEIGHT = 150;

const ToggleableModal = ({
  title,
  onClose,
  modalClass,
  children,
  autoWidth,
  containerId
}: IToggleableModalProps) => {
  const [display, setDisplay] = useState<TToggleableModalState>('expanded');
  const [bottom, setBottom] = useState<number>();
  // must manually manage position/height state because of conflict with the default height css and
  // because we must be stateful to keep call state (cannot use a key={XXX})
  const [position, setPosition] = useState<ControlPosition>({ x: 0, y: 0 });
  const [resizedHeight, setResizedHeight] = useState<number | null>(null);
  const [defaultHeight, setDefaultHeight] = useState<number>(0);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const minHeight = '50vh';

  const resetState = () => {
    setPosition({ x: 0, y: 0 });
    setResizedHeight(null);
  };

  const handleDrag = (_e: DraggableEvent, { x, y }: DraggableData) => {
    setPosition({ x, y });
  };

  const handleResize = (_e: React.SyntheticEvent, { size }: ResizeCallbackData) => {
    setResizedHeight(size.height);
  };

  const toggleExpand = () => {
    resetState();
    switch (display) {
      case 'minimized':
      case 'expanded':
        setDisplay('open');
        break;
      case 'open':
        setDisplay('expanded');
        break;
    }
  };

  const toggleMinimized = () => {
    resetState();
    setDisplay(display === 'minimized' ? 'open' : 'minimized');
  };

  const getModalClasses = () => {
    let classString = '';
    switch (display) {
      case 'minimized':
        classString += 'modal collapsed minimized ';
        break;
      case 'open':
        classString += 'modal collapsed ';
        break;
      case 'expanded':
        classString += `modal max-w-70p ${autoWidth ? 'autoWidth ' : ''}`;
        break;
    }
    classString += 'show';
    classString += ` ${modalClass}`;

    return classString;
  };

  useEffect(() => {
    const handleModalPosition = () => {
      const bottom = document?.querySelector('#fixed-footer')?.clientHeight;
      setBottom(bottom);
    };
    handleModalPosition();
    window.addEventListener('resize', handleModalPosition);
    return () => {
      return window.removeEventListener('resize', handleModalPosition);
    };
  });

  // resize the modal height if the user manually resized it, otherwise default to 100% (dynamic)
  useLayoutEffect(() => {
    if (!contentRef.current) return;
    if (display === 'minimized') {
      contentRef.current.style.removeProperty('height');
      return;
    }
    if (resizedHeight) {
      contentRef.current.style.setProperty('height', `${resizedHeight}px`, 'important');
    } else {
      contentRef.current.style.setProperty('height', minHeight, 'important');
    }
  }, [resizedHeight, display]);

  // if the modal content takes time to load, the default height is initiated with a low height
  // this grabs the real modal height if it updates and it disconnects after the 1st resize
  useLayoutEffect(() => {
    if (!contentRef.current) return;

    const observer = new ResizeObserver(() => {
      if (contentRef.current) {
        if (resizedHeight == null) {
          setDefaultHeight(contentRef.current.clientHeight);
        } else {
          observer.disconnect();
        }
      }
    });

    observer.observe(contentRef.current);
    return () => observer.disconnect();
  }, [resizedHeight]);

  const backDropClasses = display === 'expanded' ? 'modal-backdrop show' : '';
  const modalClasses = getModalClasses();
  const toggleName = display === 'expanded' ? 'collapse' : 'expand';
  const ToggleIcon = display === 'expanded' ? CollapseIcon : ExpandIcon;
  const isMinimized = display === 'minimized';

  const dialogContent = (
    <>
      <Modal.Header className="controlled">
        <Modal.Title data-test="toggle-modal-title">{title}</Modal.Title>
        <div className="controls">
          <ToggleIcon
            className="icon close"
            data-test={`toggle-modal-${toggleName}`}
            onClick={toggleExpand}
            style={{ fontSize: '15px' }}
          />
          <MinimizeIcon
            className="icon close"
            data-test="toggle-modal-minimize"
            onClick={toggleMinimized}
            style={{ fontSize: '15px' }}
          />
          <ExitPopupIcon
            className="icon close"
            data-test="toggle-modal-exit"
            onClick={onClose}
            style={{ fontSize: '15px' }}
          />
        </div>
      </Modal.Header>
      <Modal.Body
        className={(display === 'minimized' ? 'minimized' : '') + ' p-0'}
        data-test="toggle-modal-body"
      >
        {children}
      </Modal.Body>
    </>
  );

  return (
    <>
      {createPortal(
        <>
          <div className={backDropClasses}></div>
          <Draggable
            onStop={handleDrag}
            position={position}
            handle=".controlled"
            cancel=".controls"
          >
            <dialog
              id={containerId}
              className={modalClasses}
              style={{ bottom }}
              data-test="toggle-modal"
            >
              <Resizable
                height={resizedHeight || defaultHeight}
                minConstraints={[0, MIN_HEIGHT]}
                maxConstraints={[Infinity, window.innerHeight * 0.9]}
                axis={isMinimized ? 'none' : 'y'}
                resizeHandles={isMinimized ? [] : ['n']}
                onResize={handleResize}
                handle={(_axis: string, ref: React.Ref<HTMLDivElement>) => (
                  <div
                    ref={ref}
                    style={{
                      position: 'absolute',
                      top: -5,
                      left: 0,
                      width: '100%',
                      height: 10,
                      cursor: 'ns-resize',
                      zIndex: 100
                    }}
                  />
                )}
              >
                <div ref={contentRef} className="resizable-content">
                  {dialogContent}
                </div>
              </Resizable>
            </dialog>
          </Draggable>
        </>,
        document.body
      )}
    </>
  );
};

export default ToggleableModal;
